import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { ink, line, radius, font, transition } from 'features/dashboard/tokens'

const CYAN = '#33becc'

const TabNav = ({
  items = [],
  activeKey,
  onSelect,
  accent = CYAN,
  size = 'md',
  bordered = true,
  ariaLabel = 'Sections',
}) => {
  const rowRef = useRef(null)
  const measureRef = useRef(null)
  const [labelsFit, setLabelsFit] = useState(false)

  useLayoutEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure) return undefined
    const check = () => setLabelsFit(measure.scrollWidth <= row.clientWidth)
    check()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(check)
    observer.observe(row)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [items.length, items.map(item => `${item.key}:${item.count ?? ''}:${item.label}`).join('|')])

  if (!items.length) return null

  const renderContent = (item, isActive) => {
    const Icon = item.icon
    const isDisabled = !!item.disabled
    const hasCount = item.count !== undefined && item.count !== null
    return (
      <>
        {Icon && (
          <IconSlot>
            <Icon style={{ fontSize: size === 'sm' ? 15 : 17 }} />
          </IconSlot>
        )}
        <Label className='tabnav-label'>{item.label}</Label>
        {item.badge && <Badge $accent={accent}>{item.badge}</Badge>}
        {hasCount && (
          item.onCountClick ? (
            <Count
              as='span'
              role='button'
              tabIndex={0}
              $active={isActive}
              $accent={accent}
              className='tabnav-count'
              $clickable
              onClick={e => { e.stopPropagation(); item.onCountClick() }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); item.onCountClick() }
              }}
            >
              {item.count}
            </Count>
          ) : (
            <Count className='tabnav-count' $active={isActive} $accent={accent}>{item.count}</Count>
          )
        )}
        {isDisabled && <LockRoundedIcon style={{ fontSize: 13, opacity: 0.6 }} />}
      </>
    )
  }

  return (
    <Wrapper>
      <Row ref={rowRef} role='tablist' aria-label={ariaLabel} data-compact={labelsFit ? undefined : 'true'} $size={size} $bordered={bordered}>
        {items.map(item => {
          const isActive = activeKey === item.key
          const isDisabled = !!item.disabled

          const button = (
            <Tab
              type='button'
              role='tab'
              data-tour={item.dataTour}
              data-tour-id={item.dataTourId}
              aria-selected={isActive}
              aria-disabled={isDisabled}
              $active={isActive}
              $expanded={labelsFit}
              $disabled={isDisabled}
              $accent={accent}
              $size={size}
              onClick={() => {
                if (isDisabled) return
                if (item.onActivate) return item.onActivate()
                if (onSelect) onSelect(item.key)
              }}
            >
              {renderContent(item, isActive)}
            </Tab>
          )

          return item.hint ? (
            <Tooltip key={item.key} title={item.hint} placement='bottom'>
              <span>{button}</span>
            </Tooltip>
          ) : (
            <React.Fragment key={item.key}>{button}</React.Fragment>
          )
        })}
      </Row>
      <MeasureRow ref={measureRef} aria-hidden='true'>
        {items.map(item => (
          <span key={item.key}>
            <Tab
              as='span'
              $active={activeKey === item.key}
              $expanded
              $disabled={!!item.disabled}
              $accent={accent}
              $size={size}
            >
              {renderContent(item, activeKey === item.key)}
            </Tab>
          </span>
        ))}
      </MeasureRow>
    </Wrapper>
  )
}

export default TabNav

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  border-bottom: ${({ $bordered }) => ($bordered ? `1px solid ${line.base}` : 'none')};
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Tab = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  padding: ${({ $size }) => ($size === 'sm' ? '8px 12px' : '10px 14px')};
  border: none;
  background: transparent;
  color: ${({ $active, $disabled }) => ($disabled ? ink.mute : $active ? ink.base : ink.soft)};
  font-size: ${({ $size }) => ($size === 'sm' ? font.small : font.body)};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  white-space: nowrap;
  border-bottom: 2px solid ${({ $active, $accent }) => ($active ? $accent : 'transparent')};
  margin-bottom: -1px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: ${transition};

  &:hover {
    color: ${({ $disabled }) => ($disabled ? ink.mute : ink.base)};
  }

  &:focus-visible {
    outline: 2px solid ${({ $accent }) => $accent};
    outline-offset: -2px;
  }

  /* When the labelled row does not fit, collapse inactive tabs to their icon.
     Labels reveal on hover only where a real pointer can hover. */
  ${({ $active, $expanded }) => !$active && !$expanded && `
    .tabnav-label {
      max-width: 0;
      opacity: 0;
      margin-left: -6px;
      overflow: hidden;
      transition: max-width 200ms ease, opacity 140ms ease, margin-left 200ms ease;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover,
      &:focus-visible {
        .tabnav-label {
          max-width: 220px;
          opacity: 1;
          margin-left: 0;
        }
      }
    }
  `}
`

const MeasureRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  width: max-content;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
`

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 1px 6px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${({ $active, $accent }) => ($active ? `${$accent}1f` : line.base)};
  color: ${({ $active, $accent }) => ($active ? $accent : ink.mute)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'inherit')};
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: ${radius.pill};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: ${({ $accent }) => `${$accent}29`};
  color: #1f8c97;
`

const IconSlot = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`

const Label = styled.span`
  overflow: hidden;
  white-space: nowrap;
`
